import markdownIt from 'markdown-it';
import parser from 'react-html-parser';

const markdownItOptions = {
  html: true,
  breaks: true,
  linkify: true,
  typographer: true,
};

/**
 * Converts a Markdown string to one or more React elements
 * @param {string} markdown
 * @param {boolean} [allowHtml=false] - whether to allow HTML in source content
 * @param {string} [strip=false] - whether to strip a tag from the output
 * @return {HTMLElement}
 */
export const markdownToReactElement = (markdown: string, allowHtml = true) => {
  markdownItOptions.html = allowHtml;

  const html = markdownIt(markdownItOptions).renderInline(markdown);

  return parser(html);
};

const SQUARE_PRESETS = [48, 64, 128, 256, 512, 1024, 2048] as const;

const LANDING_PAGE_IMAGE_PRESETS = {
  mapBackground: 'square-2048',
  mapMarker: 'square-512',
  testimonialAvatar: 'square-256',
  testimonialCard: '3:2-sm',
} as const;

// Returns the smallest square preset that covers targetWidth, or the largest as fallback.
const squarePresetFor = (targetWidth: number): string => {
  const match = SQUARE_PRESETS.find((s) => s >= targetWidth);
  return `square-${match ?? 2048}`;
};

const resizeImageUrlWithPreset = (url: string, preset: string): string => {
  try {
    const normalizedUrl = url.startsWith('//') ? `https:${url}` : url;
    const parsed = new URL(normalizedUrl);
    if (parsed.hostname === 'images.ctfassets.net') {
      const resizedUrl = new URL(contentfulToImagesWorkerUrl(normalizedUrl));
      resizedUrl.searchParams.set('service', 'cosmos');
      resizedUrl.searchParams.set('preset', preset);
      return resizedUrl.toString();
    }
    if (parsed.hostname === 'imgdrop.imgix.net') {
      const path = parsed.pathname.replace(/^\//, '');
      const resizedUrl = new URL(
        `https://images.opendoor.com/source/s3/imgdrop-production/${path}`,
      );
      resizedUrl.searchParams.set('service', 'cosmos');
      resizedUrl.searchParams.set('preset', preset);
      return resizedUrl.toString();
    }
    if (parsed.hostname === 'images.opendoor.com') {
      parsed.searchParams.set('service', 'cosmos');
      parsed.searchParams.set('preset', preset);
      return parsed.toString();
    }
    return url;
  } catch {
    return url;
  }
};

// Convert a CMS image URL (imgdrop.imgix.net or already-migrated images.opendoor.com)
// to a CDN URL sized via the nearest square preset.
export const resizeImageUrl = (url: string, targetWidth: number): string =>
  resizeImageUrlWithPreset(url, squarePresetFor(targetWidth));

export const resizeTestimonialAvatarImageUrl = (url: string): string =>
  resizeImageUrlWithPreset(url, LANDING_PAGE_IMAGE_PRESETS.testimonialAvatar);

export const resizeTestimonialCardImageUrl = (url: string): string =>
  resizeImageUrlWithPreset(url, LANDING_PAGE_IMAGE_PRESETS.testimonialCard);

export const resizeMapBackgroundImageUrl = (url: string): string =>
  resizeImageUrlWithPreset(url, LANDING_PAGE_IMAGE_PRESETS.mapBackground);

export const resizeMapMarkerImageUrl = (url: string): string =>
  resizeImageUrlWithPreset(url, LANDING_PAGE_IMAGE_PRESETS.mapMarker);

export const contentfulToImagesWorkerUrl = (contentfulURL: string): string => {
  const normalized = contentfulURL.startsWith('//') ? `https:${contentfulURL}` : contentfulURL;
  const { pathname } = new URL(normalized);
  return `https://images.opendoor.com/source/contentful${pathname}`;
};
